<template>
	<div class="flex flex-col">
		<transition name="fade" mode="out-in" appear>
			<div class="overflow-y-auto max-h-full" v-if="slide == 1">
				<div class="bg-capgemini-blue p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Reinforce the Platform for Change</h3>
					<h2 class="text-lg font-bold">Phase 1: Business Security Strategy by Design & Default </h2>
				</div>
				<div class="p-4">
					<h4 class="font-bold text-lg mb-2">Move perception from &hellip;</h4>
					<ul class="ml-6 mb-4">
						<li class="mb-1 list-disc">Security is involved far too late in the process and as a result  risks becoming a barrier to change and transformation. </li>
						<li class="mb-1 list-disc">This restricts an organization’s ability to become agile and flexible, slows progress, and in turn diminishes the role and perception of security. </li>
						<li class="mb-1 list-disc">Security as a blocker restricts innovation, creativity, and progress. </li>
						<li class="mb-1 list-disc">As a consequence, shadow IT rises and increases the risk of stakeholders circumventing best practice.</li>
					</ul>
					<h4 class="font-bold text-lg mb-2">&hellip; and move towards</h4>
					<ul class="ml-6">
						<li class="mb-1 list-disc">Engaging security at the start of transformation processes or strategic initiatives where it will have the biggest impact.</li>
						<li class="mb-1 list-disc">Security must be involved early on, with appropriate authority, seniority, and measurement. </li>
						<li class="mb-1 list-disc">Empowering the overall business strategy by shifting security to the left of the business strategy process (in planning, execution, and new initiatives). </li>
						<li class="mb-1 list-disc">This means: security by design and default.</li>
					</ul>
				</div>
				<div class="px-2 mb-4">
					<div class="bg-gray-600 text-white p-2 font-bold text-center">
						Security must be an integral component, spanning key areas of the digital strategy of your organization as a prerequisite and measurement of success
					</div>
					<div class="flex">
						<div class="flex-1 p-3 border mr-1">Place security as a stakeholder and contributor, not as a tick box, by involving it early in business decision making as well as strategic and digital initiatives.</div>
						<div class="flex-1 p-3 border mr-1">Drive greater security collaboration and involvement in strategic decisions in order to rise the CISO function up through the business.</div>
						<div class="flex-1 p-3 border">Avoid a downward spiral where security comes late to the party and tells everyone why something won’t work. Change perceptions!</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 2">
				<div class="bg-capgemini-blue p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Blueprint for Success Across Functions</h3>
					<h2 class="text-lg font-bold">Phase 2: Build the Plan for Security Transformation</h2>
				</div>
				<div class="p-4">
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-1.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Alignment &amp; Empowerment </h4>
							<p>To achieve this, you need alignment across the functions of the business and within the C-Level. Frame the security agenda along business risks and opportunities, not technical solutions. </p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-2.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Strategic Leadership &amp; Communication</h4>
							<p>Drive and invest in buy-in and awareness, as well as highlighting successes and business enablement. Encourage feedback and act on it. CISOs must help themselves to establish their position on the board.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-3.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Business Language & Business Bias</h4>
							<p>Develop the business skills/language in security to support the transformation. Start talking their language and show willingness. Actively become a business function, delivering business value.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-4.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Coalition Across Business Functions</h4>
							<p>Build a strong cohort of supporters and evangelists across functions. Develop a network of influencers across the business. Aim at becoming part of the powerbase for business ecosystem change.</p>
						</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 3">
				<div class="bg-capgemini-blue p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Empower Security within Wider Business</h3>
					<h2 class="text-lg font-bold">Phase 3: Lead Security Change Across the Organization</h2>
				</div>
				<div class="p-4">
					<div class="flex flex-wrap">
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Create Formal Security Program Leads</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Formalized security roles that act as the business interface. They bridge the functions to ensure alignment and understanding by bringing back business cases for security to implement. </p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Align Security Metrics &amp; KPIs with Business</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Determine business-relevant security and performance metrics and KPIs. Track and report these via dashboards, not as threat detection and vulnerabilities, but as business KPIs and outcomes (time, cost, NPS, etc.).</p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Distribute  Security Responsibility</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Define security roles within the program and business unit leads. Disseminate security responsibility. </p>
								<p>Define roles and responsibilities AND show the positive impact on compliance, governance and ultimately business value. </p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Define the Security Role for your Sector</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Evangelize the broader role of security specific to your sector and its business dynamics.</p>
								<p>Demonstrate the wider risks and risk-mitigation that needs to happen within that sector and the impact this will have on business in the future.</p>
							</div>
						</div>
						<div class="w-full text-center">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">DEMONSTRATE PROGRESS</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Demonstrate progress through small wins to develop and maintain momentum and create sticky change and prevent backward steps. Consider assembling Tiger teams to drive the change and prove the concept faster.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 4">
				<div class="bg-capgemini-blue p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Drive Business Value from Security Change</h3>
					<h2 class="text-lg font-bold">Phase 4: Show Security Change as an Investment in Value</h2>
				</div>
				<div class="pb-16 pt-4 relative">
					<div class="absolute inset-0 bg-gray-200 z-0">
						<img class="h-full mx-auto" src="../assets/icons/arrow-down.svg" alt="">
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Security Evolution
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Benchmark your capability against peer organizations within the performance context to demonstrate the impact and your effectiveness.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Focus on targets and achievements. Balance technical security KPIs and metrics with relevant business metrics. Demonstrate and communicate the causal links.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Continue to demonstrate the role of security in delivering competitive advantage and shareholder value. Show the impact of security on cost mitigation and successful DX.</div>
						</div>
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Business Alignment
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Continue to focus your efforts on mitigating risk outlined in digital and business strategies. Perform horizon alignment to support and influence longer-term enterprise strategy.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Drive, encourage, and enable greater collaboration between the security function and business departments. Build security capability within LOBs.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Focus your efforts and language on key business metrics and outcome-oriented goals: speed to market, agility, cost, productivity, NPS, etc.</div>
						</div>
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Building Digital Trust
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Automate and outsource low value-add routines to focus on activities strategic for business, as well as to standardize and unify approaches across the enterprise.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">By blending security into enterprise governance, ensure you are building resilience through awareness and operational excellence.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Aim to become a strategic function of the business and a go-to source of business-enablement and strategic input, not the last to know. </div>
						</div>
					</div>					
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 5">
				<div class="bg-capgemini-blue p-4 leading-none">
					<h2 class="text-lg font-bold">Think of &hellip;</h2>
				</div>
				<div class="p-4">
					<ol class="ml-4">
						<li class="list-decimal leading-tight rounded mb-2">Formalizing the security program and integrating it with the overall governance framework</li>
						<li class="list-decimal leading-tight rounded mb-2">Distributing security liability to corporate stakeholders enabling them to evaluate and accept risks in support of business strategy</li>
						<li class="list-decimal leading-tight rounded mb-2">Building business connections with the set of cross-functional KPIs that blend security and other functions' responsibilities</li>
					</ol>
				</div>
				<div class="card">
					<div class="p-4">
						<h2 class="text-lg font-bold mb-4">Further info:</h2>
						<div class="flex">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<button type="button" @click="showCovid()">
									<p class="p-1 mb-2 font-bold">How does Covid-19 affect Security?</p>									
								</button>
							</div>
						</div>
						<div class="flex">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<a href="https://www.capgemini.com/wp-content/uploads/2019/01/The-Modern-Connected-CISO.pdf" target="_blank" @click="trackDownload">
									<p class="p-1 mb-2 font-bold">Download The Modern, Connected CISO InfoBrief</p>
									<div class="mb-2 p-2 border bg-white w-2/3">
										<img src="../assets/infobrief.png" alt="" class="">
									</div>
									
								</a>
							</div>
						</div>
						<div class="flex mt-4">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<a href="https://www.capgemini.com/cybersecurity" target="_blank" @click="trackVisit">
									<p class="p-1 font-bold">Find out more about Cybersecurity Services at capgemini.com</p>									
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</transition>
		<slot name="control"></slot>
	</div>
</template>

<script>
	export default {

		name: 'CisoSlide',
		props: ['slide', 'total'],
		data () {
			return {

			}
		},
		methods:{
			trackDownload: function(){
				this.$gtag.event('download', {
					'event_category': 'PDF',
					'event_label': 'Infobrief'
				})
			},
			trackVisit: function(){
				this.$gtag.event('click', {
					'event_category': 'Link',
					'event_label': 'https://www.capgemini.com/cybersecurity'
				})
			},
			showCovid: function(){
				this.$emit('showCovid');
			}
		}
	}
</script>

<style lang="css" scoped>
.fade-enter-active, .fade-leave-active {
	transition: opacity .3s ease;
}
.fade-enter, .fade-leave-to
/* .component-fade-leave-active below version 2.1.8 */ {
	opacity: 0;
}
</style>